<template>
  <div :class="$attrs.class">
    <label v-if="label" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
      {{ label }}
    </label>

    <!-- Native date input for mobile -->
    <input v-if="isMobile" type="date" :value="modelValue" @input="e => emit('update:modelValue', e.target.value)"
      class="md:hidden w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
             text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm
             focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition" />

    <!-- Custom trigger for desktop -->
    <button v-else type="button" ref="trigger" @click="toggle"
      class="w-full flex items-center gap-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
             text-slate-800 dark:text-white rounded-xl px-4 py-3 text-sm text-left
             focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition">
      <svg class="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
      </svg>
      <span :class="modelValue ? 'text-slate-800 dark:text-white' : 'text-slate-400'">
        {{ modelValue ? formatDisplay(modelValue) : placeholder || 'Select date' }}
      </span>
    </button>

    <!-- Calendar — teleported to body so it escapes any overflow:hidden/auto parent -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition ease-out duration-150"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95">

        <div v-if="open"
          ref="calendar"
          :style="calendarStyle"
          class="fixed z-[200] w-72 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                 rounded-2xl shadow-xl shadow-slate-200/60 dark:shadow-slate-950/60 p-4">

          <!-- Month navigation -->
          <div class="flex items-center justify-between mb-4">
            <button type="button" @click="prevMonth"
              class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5"/>
              </svg>
            </button>

            <button type="button" @click="toggleMonthYearPicker"
              class="text-sm font-semibold text-slate-800 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition">
              {{ monthNames[cursor.month] }} {{ cursor.year }}
            </button>

            <button type="button" @click="nextMonth"
              class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
              </svg>
            </button>
          </div>

          <!-- Month/year picker -->
          <div v-if="showMonthYearPicker" class="mb-3">
            <div class="flex items-center justify-between mb-2">
              <button type="button" @click="cursor.year--"
                class="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition">
                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5"/>
                </svg>
              </button>
              <span class="text-sm font-semibold text-slate-700 dark:text-slate-300">{{ cursor.year }}</span>
              <button type="button" @click="cursor.year++"
                class="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition">
                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                </svg>
              </button>
            </div>
            <div class="grid grid-cols-4 gap-1">
              <button v-for="(m, i) in monthNames" :key="m" type="button"
                @click="cursor.month = i; showMonthYearPicker = false"
                class="py-1.5 rounded-lg text-xs font-medium transition"
                :class="cursor.month === i
                  ? 'bg-indigo-500 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'">
                {{ m.slice(0, 3) }}
              </button>
            </div>
            <div class="border-t border-slate-100 dark:border-slate-800 mt-3" />
          </div>

          <!-- Day headers -->
          <div class="grid grid-cols-7 mb-1">
            <div v-for="d in dayNames" :key="d"
              class="text-center text-[10px] font-semibold text-slate-400 py-1">
              {{ d }}
            </div>
          </div>

          <!-- Days -->
          <div class="grid grid-cols-7 gap-y-1">
            <div v-for="_ in leadingBlanks" :key="'b' + _" />
            <button v-for="day in daysInMonth" :key="day" type="button"
              @click="selectDay(day)"
              class="h-8 w-full flex items-center justify-center rounded-lg text-sm font-medium transition"
              :class="dayClass(day)">
              {{ day }}
            </button>
          </div>

          <!-- Footer -->
          <div class="border-t border-slate-100 dark:border-slate-800 mt-3 pt-3 flex justify-between items-center">
            <button type="button" @click="selectToday"
              class="text-xs font-semibold text-indigo-500 hover:text-indigo-400 transition">
              Today
            </button>
            <button type="button" @click="open = false"
              class="text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition">
              Close
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: String,
  label:      String,
  placeholder: String,
})
const emit = defineEmits(['update:modelValue'])

// Detect mobile screen size
const isMobile = ref(false)
function checkMobile() {
  isMobile.value = window.innerWidth < 768 // md breakpoint
}

const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December']
const dayNames   = ['Su','Mo','Tu','We','Th','Fr','Sa']

const open   = ref(false)
const showMonthYearPicker = ref(false)
const trigger  = ref(null)
const calendar = ref(null)
const calendarStyle = ref({})

const today = new Date()
const cursor = ref({ month: today.getMonth(), year: today.getFullYear() })

function syncCursor() {
  if (props.modelValue) {
    const [y, m] = props.modelValue.split('-').map(Number)
    cursor.value = { year: y, month: m - 1 }
  }
}

async function toggle() {
  syncCursor()
  showMonthYearPicker.value = false
  open.value = !open.value
  if (open.value) {
    await nextTick()
    position()
  }
}

function position() {
  if (!trigger.value) return
  const rect = trigger.value.getBoundingClientRect()
  const calH = 360 // approximate calendar height
  const spaceBelow = window.innerHeight - rect.bottom
  const top = spaceBelow >= calH
    ? rect.bottom + 6
    : rect.top - calH - 6

  calendarStyle.value = {
    top:  `${top}px`,
    left: `${Math.min(rect.left, window.innerWidth - 296)}px`,
  }
}

function prevMonth() {
  if (cursor.value.month === 0) { cursor.value.month = 11; cursor.value.year-- }
  else cursor.value.month--
}
function nextMonth() {
  if (cursor.value.month === 11) { cursor.value.month = 0; cursor.value.year++ }
  else cursor.value.month++
}

const daysInMonth   = computed(() => new Date(cursor.value.year, cursor.value.month + 1, 0).getDate())
const leadingBlanks = computed(() => new Date(cursor.value.year, cursor.value.month, 1).getDay())

function pad(n) { return String(n).padStart(2, '0') }

function selectDay(day) {
  emit('update:modelValue', `${cursor.value.year}-${pad(cursor.value.month + 1)}-${pad(day)}`)
  open.value = false
}

function selectToday() {
  emit('update:modelValue', `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`)
  cursor.value = { month: today.getMonth(), year: today.getFullYear() }
  open.value = false
}

function formatDisplay(val) {
  if (!val) return ''
  const [y, m, d] = val.split('-')
  return `${d} ${monthNames[parseInt(m) - 1]} ${y}`
}

function dayClass(day) {
  const val      = `${cursor.value.year}-${pad(cursor.value.month + 1)}-${pad(day)}`
  const isSelected = props.modelValue === val
  const todayVal   = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
  const isToday    = val === todayVal
  if (isSelected) return 'bg-indigo-500 text-white shadow-sm shadow-indigo-300 dark:shadow-indigo-900'
  if (isToday)    return 'border border-indigo-400 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950'
  return 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
}

function onClickOutside(e) {
  if (
    trigger.value && !trigger.value.contains(e.target) &&
    calendar.value && !calendar.value.contains(e.target)
  ) {
    open.value = false
  }
}

function onScroll() { if (open.value) position() }

onMounted(() => {
  checkMobile()
  document.addEventListener('mousedown', onClickOutside)
  window.addEventListener('scroll', onScroll, true)
  window.addEventListener('resize', () => {
    checkMobile()
    position()
  })
})
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onClickOutside)
  window.removeEventListener('scroll', onScroll, true)
  window.removeEventListener('resize', position)
})
</script>
